/**
 * The Field Force role options every Manpower filter offers.
 *
 * FF roles are a closed set in the domain (`ade`, `bde`), not a maintainable
 * master data set - `MASTER_DATA_SETS` has no entry for them, so there is no
 * `/api/admin/master-data/...` collection to read. Deriving the options from
 * `FIELD_FORCE_ROLES` keeps the filter honest if the domain set ever changes,
 * and the labels reuse the same i18n keys the Initiative screens use so ADE and
 * BDE read identically everywhere (R18.13: role level only, never an employee).
 */
import { FIELD_FORCE_ROLES } from '@complan/contracts';
import type { FieldForceRole } from '@complan/domain';

export interface FfRoleOption {
  readonly id: FieldForceRole;
  readonly label: string;
  /** The i18n key, for call sites that render through `t`. */
  readonly labelKey: string;
}

const LABELS: Record<FieldForceRole, string> = {
  ade: 'ADE',
  bde: 'BDE',
};

export const FF_ROLE_OPTIONS: readonly FfRoleOption[] = FIELD_FORCE_ROLES.map(
  (role): FfRoleOption => ({
    id: role,
    label: LABELS[role],
    labelKey: `fieldForceRole.${role}`,
  }),
);

/**
 * The display label for one FF role.
 *
 * Takes a `string` rather than a {@link FieldForceRole} because some call sites hold
 * a role that arrived as an object key on the wire — a per-role demand split is a
 * `Record<string, number>`, and its keys are strings however narrow the server's own
 * type was. An unrecognised value falls back to its own uppercased text, so a role
 * added to the domain before this map is updated shows as `CDE` rather than as
 * `undefined`.
 */
export function ffRoleLabel(role: string): string {
  return LABELS[role as FieldForceRole] ?? role.toUpperCase();
}
